// Accès à l'espace ACS (13/10, maquettes validées) : #/connexion, #/verifier-email, #/activation, #/lien-expire.
// SIMULATION LOCALE : aucun e-mail n'est envoyé, aucune session, aucune authentification, aucun accord juridique, rien n'est
// enregistré hors de cet onglet. Le code reçu n'existe pas : le pied de simulation affiche le code de démonstration, vérifié
// localement (première activation → #/activation), et une commande discrète montre l'écran d'expiration. Vue focalisée : pas de dock
// ni d'actions d'en-tête (useShellImmersive, même mécanisme que l'onboarding). Composants UBM réels (Button, Input) ; contact
// du studio dans la feuille partagée (CustomSheet) avec les données publiques déjà affichées par la page Le studio.
// Conditions : aucune condition d'utilisation de l'espace membre n'est publiée par le studio (onboarding.tsx, couverture
// légale) ; l'activation renvoie aux seuls documents publics existants (CGV, confidentialité) et l'acceptation des conditions
// reste l'étape existante de la première connexion (onboarding), qui n'est pas dupliquée ici.
import { useEffect, useState, useSyncExternalStore, type FormEvent, type ReactNode } from "react";
import { Clock, Mail } from "lucide-react";
import { Button } from "@ubm/ui/components/button";
import { Input } from "@ubm/ui/components/input";
import { OtpInput } from "@ubm/ui/components/otp-input";
import { StatusBar, type Route } from "./parts";
import { CustomSheet } from "./sheet";
import { useAccount } from "./compte-state";
import { useShellImmersive } from "./shell-state";
import { ADDRESS } from "./studio-fixtures";

interface PageProps { go: (route: Route) => void; onOutOfScope: (label: string) => void }

/** Local demo state of the access flow (this tab only). */
interface AuthState { email: string; activated: boolean; resentAt: number }
let state: AuthState = { email: "", activated: false, resentAt: 0 };
const listeners = new Set<() => void>();
const auth = {
  get: () => state,
  set: (p: Partial<AuthState>) => { state = { ...state, ...p }; listeners.forEach(l => l()); },
  subscribe: (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; },
};
const useAuth = () => useSyncExternalStore(auth.subscribe, auth.get, auth.get);

const RESEND_COOLDOWN_MS = 30_000;
/** Demo code of the local simulation (shown in the simulation footer, never a real secret). */
const DEMO_CODE = "123456";
const CODE_LENGTH = 6;
const EMAIL_RE = /^\S+@\S+\.\S+$/;
const to = (hash: string) => { window.location.hash = hash; };
/** The onboarding keeps its own configuration (?mode=…) when the activation was opened with it. */
const modeQuery = () => { const m = window.location.hash.match(/mode=(obligatoire|facultative)/)?.[1]; return m ? `?mode=${m}` : ""; };
// Public studio documents, as listed by the onboarding (docs/partners/acs-studio/couverture-legales.md).
const PUBLIC_DOCS = [
  { label: "Conditions générales de vente", href: "https://www.acs-studio.bzh/conditions-generales-de-vente/" },
  { label: "Politique de confidentialité", href: "https://www.acs-studio.bzh/politique-de-confidentialite/" },
];

/** Shared frame of the four screens: status bar, open page (no card, no dock, no header actions), studio contact sheet. */
function AuthFrame({ children, demo }: { children: ReactNode; demo?: ReactNode }) {
  useShellImmersive(true);
  const [contact, setContact] = useState(false);
  return <div className="cc-screen cc-auth">
    <StatusBar />
    <div className="cc-scroll">
      <div className="cc-auth-body">{children}</div>
      <button type="button" className="cc-auth-link cc-auth-contact" onClick={() => setContact(true)}>Contacter le studio</button>
      {/* Discreet simulation mention on every access screen, outside the screen's own copy; demo controls, if any, under it. */}
      <div className="cc-auth-demo" {...(demo ? { role: "group", "aria-label": "Simulation locale" } : {})}>
        <span>Simulation locale : aucun e-mail ni accès réel.</span>{demo}</div>
    </div>
    {contact && <CustomSheet title="Contacter le studio" onClose={() => setContact(false)} overlayClassName="cc-home-overlay" className="cc-home-sheet">
      <p className="cc-home-p"><b>ACS Studio</b><br />{ADDRESS.line}<br />{ADDRESS.city}</p>
      <p className="cc-home-demo">Prototype : contenu fictif, rien n'est envoyé.</p>
    </CustomSheet>}
  </div>;
}

/** #/connexion */
export function Connexion(_: PageProps) {
  const a = useAuth();
  const [email, setEmail] = useState(a.email);
  const [tried, setTried] = useState(false);
  const valid = EMAIL_RE.test(email.trim());
  const submit = (e: FormEvent) => {
    e.preventDefault(); setTried(true);
    if (!valid) return;
    auth.set({ email: email.trim() });
    to("#/verifier-email");
  };
  return <AuthFrame>
    <form className="cc-auth-form" noValidate onSubmit={submit}>
      <h1 className="cc-auth-title">Connecte-toi à ton espace ACS</h1>
      <Input label="Adresse e-mail" type="email" autoComplete="email" inputMode="email" value={email}
        onChange={e => setEmail(e.target.value)} description="Utilise l’adresse communiquée au studio."
        error={tried && !valid ? "Indique une adresse e-mail valide." : undefined} />
      <Button type="submit" variant="primary" size="lg">Continuer</Button>
    </form>
  </AuthFrame>;
}

/** #/verifier-email — recomposed (13/10, correction): a 6-digit code typed in the REAL UBM OtpInput (keyboard, paste, one-time-code
 *  autofill, autofocus), checked LOCALLY against the demo code shown in the simulation footer. Right code: first activation →
 *  #/activation, already activated in this demo → #/accueil. Wrong code: inline error under the field. Nothing is sent. */
export function VerifierEmail(_: PageProps) {
  const a = useAuth();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [now, setNow] = useState(() => Date.now());
  const cooling = a.resentAt > 0 && now - a.resentAt < RESEND_COOLDOWN_MS;
  // The cooldown ends on its own; one interval while it runs, cleared afterwards.
  useEffect(() => { if (!cooling) return; const t = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(t); }, [cooling]);
  useEffect(() => { if (!a.email) to("#/connexion"); }, [a.email]);
  const left = Math.ceil((RESEND_COOLDOWN_MS - (now - a.resentAt)) / 1000);
  const complete = code.length === CODE_LENGTH;
  const verify = (e: FormEvent) => {
    e.preventDefault();
    if (!complete) return;
    if (code !== DEMO_CODE) { setError("Ce code ne correspond pas. Vérifie les 6 chiffres."); return; }
    to(a.activated ? "#/accueil" : "#/activation");
  };
  return <AuthFrame demo={<>
    <span>Code de démonstration : {DEMO_CODE}</span>
    <button type="button" onClick={() => to("#/lien-expire")}>Lien expiré</button>
  </>}>
    <span className="cc-auth-icon" aria-hidden="true"><Mail size={26} strokeWidth={1.75} /></span>
    <h1 className="cc-auth-title">Vérifie tes e-mails</h1>
    <p className="cc-auth-p">Saisis le code envoyé à <span className="cc-auth-email">{a.email}</span>{" "}
      <button type="button" className="cc-auth-inline" onClick={() => to("#/connexion")}>Modifier</button></p>
    <form className="cc-auth-otp" noValidate onSubmit={verify}>
      <OtpInput length={CODE_LENGTH} label="Code à 6 chiffres" value={code} autoFocus error={error}
        onChange={v => { setCode(v); if (error) setError(undefined); }} />
      <Button type="submit" variant="primary" size="lg" disabled={!complete}>Vérifier</Button>
    </form>
    <div className="cc-auth-resend">
      <button type="button" className="cc-auth-link" disabled={cooling} onClick={() => { auth.set({ resentAt: Date.now() }); setNow(Date.now()); }}>Renvoyer le code</button>
      <p className="cc-auth-status" role="status">{cooling ? `Code renvoyé (simulation) · nouvel envoi dans ${left} s` : ""}</p>
    </div>
    <p className="cc-auth-note">Pense à vérifier tes courriers indésirables.</p>
  </AuthFrame>;
}

/** #/activation */
export function Activation(_: PageProps) {
  const a = useAuth();
  const acct = useAccount();
  const email = a.email || acct.email;
  return <AuthFrame>
    <h1 className="cc-auth-title">Bienvenue {acct.firstName}</h1>
    <p className="cc-auth-p">Ton accès au studio est prêt.</p>
    <div className="cc-auth-form">
      <Input label="Adresse e-mail" type="email" value={email} readOnly />
      <p className="cc-auth-docs">{PUBLIC_DOCS.map((d, i) => <span key={d.href}>{i > 0 && " · "}<a href={d.href} target="_blank" rel="noreferrer">{d.label}</a></span>)}</p>
      <Button variant="primary" size="lg" onClick={() => { auth.set({ activated: true, email }); to(`#/onboarding${modeQuery()}`); }}>Activer mon espace</Button>
    </div>
    <button type="button" className="cc-auth-link" onClick={() => { auth.set({ email: "" }); to("#/connexion"); }}>Ce n’est pas ton compte ?</button>
  </AuthFrame>;
}

/** #/lien-expire */
export function LienExpire(_: PageProps) {
  const a = useAuth();
  return <AuthFrame>
    <span className="cc-auth-icon" aria-hidden="true"><Clock size={26} strokeWidth={1.75} /></span>
    <h1 className="cc-auth-title">Ce lien a expiré</h1>
    <p className="cc-auth-p">Demande un nouveau lien pour accéder à ton espace ACS.</p>
    <div className="cc-auth-actions">
      <Button variant="primary" size="lg" onClick={() => { if (a.email) { auth.set({ resentAt: Date.now() }); to("#/verifier-email"); } else to("#/connexion"); }}>Recevoir un nouveau lien</Button>
      <button type="button" className="cc-auth-link" onClick={() => { auth.set({ email: "" }); to("#/connexion"); }}>Utiliser une autre adresse</button>
    </div>
  </AuthFrame>;
}
